import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { BusinessConfig } from '@/interfaces/billing'

import APIServices from '../../api/APIServices'

export interface BusinessConfigQueryParams {
  businessId: number
  getAllConfigs?: boolean
}

// Create a unique, descriptive key for this query
const BUSINESS_CONFIGS_QUERY_KEY = 'business-configs'

/**
 * Fetch business configs from the API
 * Separating API call logic from the hook for better testability
 */
const fetchBusinessConfigs = async ({
  getAllConfigs = false,
}: {
  getAllConfigs?: boolean
}): Promise<BusinessConfig[]> => {
  const response = await APIServices.get(
    `/administration/business/configs${getAllConfigs ? '/all' : ''}`
  )
  return response.data
}

/**
 * Custom hook for fetching business configs
 *
 * @param params - Query parameters for filtering configs
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useBusinessConfigsQuery = ({
  params,
  options,
}: {
  params: BusinessConfigQueryParams
  options?: Partial<UseQueryOptions<BusinessConfig[], AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<BusinessConfig[], AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [BUSINESS_CONFIGS_QUERY_KEY, params],

    // Separate the API call logic from the query configuration
    queryFn: () =>
      fetchBusinessConfigs({ getAllConfigs: params?.getAllConfigs }),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    businessConfigs: queryResult.data || [],
    isLoadingBusinessConfigs: queryResult.isLoading,
    isErrorBusinessConfigs: queryResult.isError,
    refetchBusinessConfigs: queryResult.refetch,
  }
}

/**
 * Helper function to get the invoice query key
 * Useful for cache invalidation or prefetching
 */
export const getBusinessConfigsQueryKey = (
  params?: BusinessConfigQueryParams
) => [BUSINESS_CONFIGS_QUERY_KEY, params]
